/**
 * AI Generation Tools
 * Tools for generating text, images, and videos using Cosmic AI
 */

import { z } from 'zod';
import { getCosmicClient, requireWriteAccess } from '../client.js';
import { formatToolError } from '../errors.js';
import type { ToolResult } from '../types.js';
import { additiveTool } from './annotations.js';

// Schema definitions for tool inputs
export const generateTextSchema = z.object({
  prompt: z.string().describe('The prompt to generate text from'),
  model: z
    .string()
    .optional()
    .describe('AI model to use (e.g., "gpt-4", "claude-3-opus")'),
  max_tokens: z
    .number()
    .min(1)
    .max(100000)
    .optional()
    .describe('Maximum number of tokens to generate'),
});

export const generateImageSchema = z.object({
  prompt: z.string().describe('The prompt describing the image to generate'),
  format: z
    .enum(['png', 'svg'])
    .optional()
    .describe('Output format. Use svg for logos, icons, illustrations, and vector artwork. Default: png'),
  model: z
    .string()
    .optional()
    .describe('AI model to use for PNG generation. Options: gemini-3.1-flash-image-preview (default, recommended), gemini-3-pro-image-preview, dall-e-3. Ignored for SVG.'),
  aspect_ratio: z
    .enum(['1:1', '16:9', '9:16', '4:3', '3:4'])
    .optional()
    .describe('Aspect ratio. For SVG this sets the viewBox. Default: 1:1'),
  folder: z
    .string()
    .optional()
    .describe('Folder to save the generated image to'),
  metadata: z
    .record(z.unknown())
    .optional()
    .describe('Additional metadata for the generated image'),
  alt_text: z
    .string()
    .optional()
    .describe('Alt text for the generated image'),
});

export const generateVideoSchema = z.object({
  prompt: z.string().describe('The prompt describing the video to generate'),
  model: z
    .string()
    .optional()
    .describe('AI model to use for video generation'),
  duration: z
    .enum(['4', '6', '8'])
    .optional()
    .describe('Video duration in seconds'),
  resolution: z
    .enum(['720p', '1080p'])
    .optional()
    .describe('Video resolution'),
  folder: z
    .string()
    .optional()
    .describe('Folder to save the generated video to'),
  metadata: z
    .record(z.unknown())
    .optional()
    .describe('Additional metadata for the generated video'),
});

export const generateAudioSchema = z.object({
  prompt: z.string().describe('The text to convert to speech'),
  voice: z
    .enum([
      'alloy', 'ash', 'coral', 'echo', 'fable',
      'nova', 'onyx', 'sage', 'shimmer',
    ])
    .optional()
    .describe('Voice to use for speech generation (default: nova)'),
  model: z
    .enum(['tts-1', 'tts-1-hd'])
    .optional()
    .describe('TTS model to use (default: tts-1)'),
  folder: z
    .string()
    .optional()
    .describe('Folder to save the generated audio to'),
  metadata: z
    .record(z.unknown())
    .optional()
    .describe('Additional metadata for the generated audio'),
});

// Tool definitions
export const aiTools = [
  {
    name: 'cosmic_ai_generate_text',
    ...additiveTool('Generate text'),
    description:
      'Generate text content using Cosmic AI. Useful for creating content, descriptions, summaries, and more.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        prompt: {
          type: 'string',
          description: 'The prompt to generate text from',
        },
        model: {
          type: 'string',
          description: 'AI model to use (e.g., "gpt-4", "claude-3-opus")',
        },
        max_tokens: {
          type: 'number',
          description: 'Maximum number of tokens to generate',
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'cosmic_ai_generate_image',
    ...additiveTool('Generate image'),
    description:
      'Generate an image using Cosmic AI and automatically upload it to your media library. Use format "svg" for logos, icons, illustrations, and vector artwork. For PNG display media.imgix_url; for SVG display media.url (imgix does not serve SVG). Requires write access.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        prompt: {
          type: 'string',
          description: 'The prompt describing the image to generate',
        },
        format: {
          type: 'string',
          enum: ['png', 'svg'],
          description: 'Output format. Use svg for logos, icons, illustrations, and vector artwork. Default: png',
        },
        model: {
          type: 'string',
          description: 'AI model to use for PNG generation. Options: gemini-3.1-flash-image-preview (default, recommended), gemini-3-pro-image-preview, dall-e-3. Ignored for SVG.',
        },
        aspect_ratio: {
          type: 'string',
          enum: ['1:1', '16:9', '9:16', '4:3', '3:4'],
          description: 'Aspect ratio. For SVG this sets the viewBox. Default: 1:1',
        },
        folder: {
          type: 'string',
          description: 'Folder to save the generated image to',
        },
        metadata: {
          type: 'object',
          description: 'Additional metadata for the generated image',
        },
        alt_text: {
          type: 'string',
          description: 'Alt text for the generated image',
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'cosmic_ai_generate_video',
    ...additiveTool('Generate video'),
    description:
      'Generate a video using Cosmic AI (powered by Veo) and automatically upload it to your media library. Requires write access.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        prompt: {
          type: 'string',
          description: 'The prompt describing the video to generate',
        },
        model: {
          type: 'string',
          description: 'AI model to use for video generation',
        },
        duration: {
          type: 'string',
          enum: ['4', '6', '8'],
          description: 'Video duration in seconds (4, 6, or 8)',
        },
        resolution: {
          type: 'string',
          enum: ['720p', '1080p'],
          description: 'Video resolution',
        },
        folder: {
          type: 'string',
          description: 'Folder to save the generated video to',
        },
        metadata: {
          type: 'object',
          description: 'Additional metadata for the generated video',
        },
      },
      required: ['prompt'],
    },
  },
  {
    name: 'cosmic_ai_generate_audio',
    ...additiveTool('Generate audio'),
    description:
      'Generate audio from text using OpenAI text-to-speech and upload it to your media library. Supports 13 natural-sounding voices. Requires write access.',
    inputSchema: {
      type: 'object' as const,
      properties: {
        prompt: {
          type: 'string',
          description: 'The text to convert to speech',
        },
        voice: {
          type: 'string',
          enum: [
            'alloy', 'ash', 'coral', 'echo', 'fable',
            'nova', 'onyx', 'sage', 'shimmer',
          ],
          description: 'Voice to use for speech generation (default: nova)',
        },
        model: {
          type: 'string',
          enum: ['tts-1', 'tts-1-hd'],
          description: 'TTS model to use (default: tts-1)',
        },
        folder: {
          type: 'string',
          description: 'Folder to save the generated audio to',
        },
        metadata: {
          type: 'object',
          description: 'Additional metadata for the generated audio',
        },
      },
      required: ['prompt'],
    },
  },
];

// Tool handlers
export async function handleGenerateText(
  params: z.infer<typeof generateTextSchema>
): Promise<ToolResult> {
  try {
    const cosmic = getCosmicClient();

    const response = await cosmic.ai.generateText({
      prompt: params.prompt,
      model: params.model,
      max_tokens: params.max_tokens,
    });

    return {
      content: [
        {
          type: 'text',
          text: JSON.stringify(
            {
              message: 'Text generated successfully',
              text: response.text,
              model: response.model,
              usage: response.usage,
            },
            null,
            2
          ),
        },
      ],
    };
  } catch (error) {
    const message = formatToolError(error);
    return {
      content: [{ type: 'text', text: `Error generating text: ${message}` }],
      isError: true,
    };
  }
}

export async function handleGenerateImage(
  params: z.infer<typeof generateImageSchema>
): Promise<ToolResult> {
  try {
    requireWriteAccess();

    const cosmic = getCosmicClient();

    const response = await cosmic.ai.generateImage({
      prompt: params.prompt,
      model: params.model,
      folder: params.folder,
      metadata: params.metadata,
      alt_text: params.alt_text,
      ...(params.format ? { format: params.format } : {}),
      ...(params.aspect_ratio ? { aspect_ratio: params.aspect_ratio } : {}),
    });

    return {
      content: [
        {
          type: 'text',
          text: JSON.stringify(
            {
              message: 'Image generated successfully',
              media: response.media,
            },
            null,
            2
          ),
        },
      ],
    };
  } catch (error) {
    const message = formatToolError(error);
    return {
      content: [{ type: 'text', text: `Error generating image: ${message}` }],
      isError: true,
    };
  }
}

export async function handleGenerateVideo(
  params: z.infer<typeof generateVideoSchema>
): Promise<ToolResult> {
  try {
    requireWriteAccess();

    const cosmic = getCosmicClient();

    // Convert duration string to number if provided
    const duration = params.duration
      ? (parseInt(params.duration, 10) as 4 | 6 | 8)
      : undefined;

    const response = await cosmic.ai.generateVideo({
      prompt: params.prompt,
      model: params.model,
      duration,
      resolution: params.resolution,
      folder: params.folder,
      metadata: params.metadata,
    });

    return {
      content: [
        {
          type: 'text',
          text: JSON.stringify(
            {
              message: 'Video generated successfully',
              media: response.media,
              usage: response.usage,
            },
            null,
            2
          ),
        },
      ],
    };
  } catch (error) {
    const message = formatToolError(error);
    return {
      content: [{ type: 'text', text: `Error generating video: ${message}` }],
      isError: true,
    };
  }
}

export async function handleGenerateAudio(
  params: z.infer<typeof generateAudioSchema>
): Promise<ToolResult> {
  try {
    requireWriteAccess();

    const cosmic = getCosmicClient();

    const response = await cosmic.ai.generateAudio({
      prompt: params.prompt,
      voice: params.voice,
      model: params.model,
      folder: params.folder,
      metadata: params.metadata,
    });

    return {
      content: [
        {
          type: 'text',
          text: JSON.stringify(
            {
              message: 'Audio generated successfully',
              media: response.media,
              usage: response.usage,
            },
            null,
            2
          ),
        },
      ],
    };
  } catch (error) {
    const message = formatToolError(error);
    return {
      content: [{ type: 'text', text: `Error generating audio: ${message}` }],
      isError: true,
    };
  }
}
